import { Drop, PersonSimpleWalk, GraduationCap } from "@phosphor-icons/react";
import {
  FLOOD_BADGE_CLASS,
  FLOOD_LABEL,
  WALK_BADGE_CLASS,
  WALK_LABEL,
} from "@/lib/constants";

const NYC_REGIONS = new Set(["The Bronx", "Manhattan", "Brooklyn", "Queens", "Staten Island"]);

function Badge({ children, tone, compact, testid, title }) {
  const size = compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span
      data-testid={testid}
      title={title}
      className={`inline-flex items-center gap-1 rounded-sm border ${size} uppercase tracking-wider ${tone}`}
    >
      {children}
    </span>
  );
}

function compactDistrict(district, compact) {
  if (!district) return null;
  if (compact) return String(district).replace(/^District\s+/i, "Dist. ");
  return String(district);
}

/**
 * Neighborhood context chips — flood / walk / school district. Uses the field
 * names emitted by backend `enrich_property_neighborhood`
 * (`flood_risk_band`, `walkability_band`, `school_district`).
 *
 * `showUnavailable` renders faint "N/A" chips when the property is inside an
 * NYC borough but a signal isn't cached yet — makes the detail page honest
 * about which layers apply.
 */
export default function PropertyContextBadges({
  property,
  compact = false,
  showUnavailable = false,
  className = "",
}) {
  // Accept both field naming conventions (flood_risk_band from old model, flood_risk from new).
  const rawFlood = property?.flood_risk || property?.flood_risk_band || "";
  const floodBand = rawFlood && rawFlood !== "n/a" && rawFlood !== "unknown"
    ? String(rawFlood).toLowerCase()
    : null;
  // walkability_band is the canonical field; walk_band is the legacy alias.
  const rawWalk = property?.walkability_band || property?.walk_band || "";
  const walkBand = rawWalk && rawWalk !== "n/a"
    ? String(rawWalk).toLowerCase()
    : null;
  const district = property?.school_district || null;
  const inNyc = NYC_REGIONS.has(property?.region);
  const badges = [];

  if (floodBand) {
    badges.push(
      <Badge
        key="flood"
        compact={compact}
        tone={FLOOD_BADGE_CLASS[floodBand] || FLOOD_BADGE_CLASS.unknown}
        testid={`badge-flood-${property?.id || "detail"}`}
        title={property.flood_risk_label || `Flood ${floodBand}`}
      >
        <Drop size={11} weight="duotone" /> {FLOOD_LABEL[floodBand] || FLOOD_LABEL.unknown}
      </Badge>,
    );
  } else if (showUnavailable && inNyc) {
    badges.push(
      <Badge key="flood-na" compact={compact} tone={FLOOD_BADGE_CLASS.unknown}
             testid={`badge-flood-${property?.id || "detail"}`}>
        <Drop size={11} weight="duotone" /> {FLOOD_LABEL.unknown}
      </Badge>,
    );
  }

  if (walkBand) {
    badges.push(
      <Badge
        key="walk"
        compact={compact}
        tone={WALK_BADGE_CLASS[walkBand] || WALK_BADGE_CLASS.low}
        testid={`badge-walk-${property?.id || "detail"}`}
        title={property.walkability_basis || "Free walkability proxy — transit + amenity density (not Walk Score API)."}
      >
        <PersonSimpleWalk size={11} weight="duotone" /> {WALK_LABEL[walkBand] || walkBand}
      </Badge>,
    );
  }

  if (district) {
    badges.push(
      <Badge
        key="school"
        compact={compact}
        tone="border-brand/25 text-brand bg-brand/10"
        testid={`badge-school-${property?.id || "detail"}`}
        title="NYC Community School District (NYC DOE open data)"
      >
        <GraduationCap size={11} weight="duotone" /> {compactDistrict(district, compact)}
      </Badge>,
    );
  } else if (showUnavailable && inNyc) {
    badges.push(
      <Badge key="school-na" compact={compact} tone="border-white/10 text-white/40 bg-white/5"
             testid={`badge-school-${property?.id || "detail"}`}>
        <GraduationCap size={11} weight="duotone" /> Dist. N/A
      </Badge>,
    );
  }

  if (!badges.length) return null;

  return (
    <div
      className={`flex flex-wrap gap-2 ${className}`.trim()}
      data-testid={`property-context-badges-${property?.id || "detail"}`}
    >
      {badges}
    </div>
  );
}
